// SEO Component for setting page-specific metadata
// Created by Claude on 20.05.2025 for SEO optimization

import { Helmet } from 'react-helmet';
import PropTypes from 'prop-types';
import { SITE_TITLE, SITE_DESCRIPTION, IMAGE_BASE_URL } from '../../config';
import { getUserLanguage } from '../../utils/language';

/**
 * SEO component that handles all page metadata/SEO needs
 */
const SEO = ({
  title,
  description,
  image,
  article = false,
  keywords,
  canonical,
  schema,
  noindex = false,
}) => {
  const language = getUserLanguage();
  const siteName = 'NO RISK NO DRIFT';
  const defaultTitle = SITE_TITLE;
  const defaultDescription = SITE_DESCRIPTION;
  const defaultImage = `${IMAGE_BASE_URL}/images/logo.jpeg`;
  const siteUrl = language === 'cs' ? 'https://norisknodrift.cz' : IMAGE_BASE_URL;
  
  const seo = {
    title: title ? `${title} | ${siteName}` : defaultTitle,
    description: description || defaultDescription,
    image: `${image || defaultImage}`,
    url: canonical ? canonical : typeof window !== 'undefined' ? window.location.href : '',
    keywords: keywords || 'drift, drifting, drift team, drift česká lípa, drift show',
  };

  const locale = language === 'cs' ? 'cs_CZ' : 'en_US';

  return (
    <Helmet>
      <html lang={language} />
      <title>{seo.title}</title>
      <meta name="description" content={seo.description} />
      <meta name="keywords" content={seo.keywords} />
      
      {noindex && <meta name="robots" content="noindex, nofollow" />}
      {!noindex && <meta name="robots" content="index, follow" />}
      
      {canonical && <link rel="canonical" href={canonical} />}
      
      <meta property="og:site_name" content={siteName} />
      <meta property="og:title" content={seo.title} />
      <meta property="og:description" content={seo.description} />
      <meta property="og:image" content={seo.image} />
      <meta property="og:url" content={seo.url} />
      <meta property="og:type" content={article ? 'article' : 'website'} />
      <meta property="og:locale" content={locale} />
      
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={seo.title} />
      <meta name="twitter:description" content={seo.description} />
      <meta name="twitter:image" content={seo.image} />
      
      {/* Structured data */}
      {schema && (
        <script type="application/ld+json">
          {JSON.stringify(schema)}
        </script>
      )}
    </Helmet>
  );
};

SEO.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  image: PropTypes.string,
  article: PropTypes.bool,
  keywords: PropTypes.string,
  canonical: PropTypes.string,
  schema: PropTypes.object,
  noindex: PropTypes.bool,
};

export default SEO;